import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withKeys as _withKeys, unref as _unref, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  id: "quiz",
  tabindex: "-1",
  "aria-label": "Quiz"
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "column" }
const _hoisted_4 = {
  key: 0,
  class: "quiz"
}
const _hoisted_5 = {
  key: 0,
  class: "question"
}
const _hoisted_6 = {
  key: 0,
  class: "question__title"
}
const _hoisted_7 = { class: "question__type" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = {
  key: 1,
  class: "question__code"
}
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = {
  key: 2,
  class: "question__image"
}
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = ["aria-selected", "onClick", "onKeyup"]
const _hoisted_16 = { class: "question__footer" }
const _hoisted_17 = { class: "actions" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { key: 0 }
const _hoisted_21 = { key: 1 }
const _hoisted_22 = {
  key: 0,
  class: "meta"
}

import config from '@/config.json';
import { computed, onBeforeMount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Quiz } from '@/models/Quiz';
import { QuizAnswer } from '@/models/QuizAnswer';
import { progress } from '@/composables/progress';


export default /*@__PURE__*/_defineComponent({
  __name: 'QuizComponent',
  props: {
    quizzes: {}
  },
  emits: ["selectAnswer", "updateScore"],
  setup(__props: any, { emit: __emit }) {

const router = useRouter();
const route = useRoute();
const props = __props;

const emit = __emit;

onBeforeMount (() => {
  const questionIndex = parseInt(route.params.questionIndex as string);
  const quiz = props.quizzes.find((quiz: Quiz) => quiz.id === route.params.id);

  if (!quiz || typeof quiz?.questions[questionIndex] === 'undefined') {
    router.replace({ name: 'home' });
  }

  if (quiz && config.shuffleAnswers) {
    shuffleAnswers(quiz);
  }
});

const quiz = computed(() => {
  return props.quizzes.find((quiz: Quiz) => quiz.id === route.params.id);
});

const questionIndex = computed(() => {
  return parseInt(route.params.questionIndex as string);
});

const questionText = computed(() => {
  return quiz.value?.questions
      ? quiz?.value.questions[questionIndex.value]
          ?.text.replace('___', '<span class="gap"></span>')
      : 'Frage nicht gefunden'
    ;
});

const hasQuestion = computed(() => {
  return quiz.value?.questions[questionIndex.value];
});

const hasNextQuestion = computed(() => {
  return quiz.value?.questions[questionIndex.value + 1];
});

const answers = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.answers;
});

const image = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.image;
});

const hasImage = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.image;
});

const altText = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.altText || '';
});

const hasCode = computed(() => {
  return quiz.value?.questions[questionIndex.value]?.code;
});

const codeLanguage = computed(() => {
  return hasCode.value ? 'language-' + quiz.value?.questions[questionIndex.value].code.language : '';
});

const isSingleChoice = computed(() => {
  return hasQuestion.value
    && answers.value
    && quiz.value?.questions[questionIndex.value].answers
      .filter((answer: QuizAnswer) => answer.correct)
      .length === 1;
});

const code = computed(() => {
  let code = '';

  quiz.value?.questions[questionIndex.value].code?.lines
    .forEach((line: string) => {
      code += `<span class="dive-code">${ line }</span><br />`
    });

  return code;
});

const selectAnswer = (index: number) => {
  emit('selectAnswer', {
    quizId: quiz.value?.id,
    questionIndex: questionIndex.value,
    answerIndex: index,
  });
};

const isAnswered = (): boolean => {
  const answered = answers.value?.filter((answer: QuizAnswer) => answer.selected);

  return answered ? answered.length > 0 : false;
};

const nextQuestion = async () => {
  await router.push({
    name: 'quiz',
    params: {
      id: quiz.value?.id,
      questionIndex: questionIndex.value + 1,
    },
  });

  document.getElementById('quiz')?.focus();
};

const showResult = async () => {
  if (quiz.value) {
    quiz.value.completed = true;

    emit('updateScore', quiz.value);

    await router.push({
      name: 'result',
      params: {
        id: quiz.value.id,
        score: Math.round(quiz.value.score),
      },
    });

    document.getElementById('quiz-result')?.focus();
  }
}

const shuffleAnswers = (quiz: Quiz) => {
  if (!quiz) {
    return;
  }

  quiz.questions.forEach((question) => {
    const answers = question.answers;
    let currentIndex = answers.length, randomIndex: number;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      [answers[currentIndex], answers[randomIndex]] = [answers[randomIndex], answers[currentIndex]];
    }
  });
}

return (_ctx: any,_cache: any) => {
  const _directive_highlightjs = _resolveDirective("highlightjs")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (quiz.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (hasQuestion.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (questionText.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("p", _hoisted_7, [
                            (isSingleChoice.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Single Choice"))
                              : (_openBlock(), _createElementBlock("span", _hoisted_9, "Multiple Choice"))
                          ]),
                          _createElementVNode("h3", { innerHTML: questionText.value }, null, 8, _hoisted_10)
                        ]))
                      : _createCommentVNode("", true),
                    (hasCode.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _withDirectives((_openBlock(), _createElementBlock("pre", {
                            class: _normalizeClass(codeLanguage.value)
                          }, [
                            _cache[2] || (_cache[2] = _createTextVNode("                ")),
                            _createElementVNode("code", {
                              class: "javascript",
                              innerHTML: code.value
                            }, null, 8, _hoisted_12),
                            _cache[3] || (_cache[3] = _createTextVNode("\n              "))
                          ], 2)), [
                            [_directive_highlightjs]
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    (hasImage.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _createElementVNode("img", {
                            src: require(`@/assets/quiz/images/${quiz.value.id}/${image.value}`),
                            alt: altText.value
                          }, null, 8, _hoisted_14)
                        ]))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(answers.value, (answer, answerIndex) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: _normalizeClass(["question__answer", { selected: answer.selected, singleChoice: isSingleChoice.value }]),
                        "aria-selected": answer.selected,
                        onClick: ($event: any) => (selectAnswer(answerIndex)),
                        onKeyup: _withKeys(($event: any) => (selectAnswer(answerIndex)), ["enter","space"]),
                        key: answerIndex,
                        tabIndex: "0",
                        role: "button"
                      }, _toDisplayString(answer.text), 43, _hoisted_15))
                    }), 128)),
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("div", _hoisted_17, [
                        (hasNextQuestion.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              onClick: _cache[0] || (_cache[0] = ($event: any) => (nextQuestion())),
                              disabled: !isAnswered()
                            }, " Nächste Frage ", 8, _hoisted_18))
                          : _createCommentVNode("", true),
                        (!hasNextQuestion.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (showResult())),
                              disabled: !isAnswered()
                            }, [
                              (!isAnswered())
                                ? (_openBlock(), _createElementBlock("span", _hoisted_20, "Antworten wählen"))
                                : (_openBlock(), _createElementBlock("span", _hoisted_21, "Zur Auswertung"))
                            ], 8, _hoisted_19))
                          : _createCommentVNode("", true)
                      ]),
                      (quiz.value.questions)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            _createElementVNode("small", null, [
                              _createElementVNode("strong", null, _toDisplayString(questionIndex.value + 1), 1),
                              _cache[4] || (_cache[4] = _createTextVNode(" von ")),
                              _createElementVNode("strong", null, _toDisplayString(quiz.value.questions.length), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        class: "progress-bar",
                        style: _normalizeStyle({ 'width': _unref(progress)(quiz.value) + '%' })
                      }, null, 4)
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})